import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useSiteSettings, buildWhatsAppLink } from "@/lib/SiteSettingsContext";
import { cleanContact, hasAnyContact } from "@/lib/placeholders";
import ContactForm from "@/components/public/ContactForm";
import SEO from "@/components/public/SEO";

export default function Contact() {
    const { settings } = useSiteSettings();
    const c = cleanContact(settings);
    const anyContact = hasAnyContact(c);
    const waHref = c.whatsapp
        ? buildWhatsAppLink(c.whatsapp, settings.whatsapp_default_message)
        : null;

    return (
        <div data-testid="contact-page">
            <SEO
                title="Tell us about your business | Omega IT"
                description="Tell us a bit about your business and we'll come back within one working day. No obligation, no spam."
            />
            <section className="px-5 sm:px-8 lg:px-12 pt-16 pb-8 lg:pt-24">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="eyebrow" data-testid="contact-eyebrow">— GET IN TOUCH</div>
                    <h1
                        className="font-display mt-4"
                        style={{ fontSize: "clamp(34px, 5vw, 56px)", fontWeight: 700, lineHeight: 1.06, color: "var(--text)" }}
                        data-testid="contact-heading"
                    >
                        Tell us about your business
                    </h1>
                    <p style={{ color: "var(--muted)", fontSize: 16, marginTop: 18, lineHeight: 1.6 }}>
                        Tell us a bit about your business and we'll come back within one working day.
                    </p>
                </div>
            </section>

            <section className="px-5 sm:px-8 lg:px-12 pb-20">
                <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7">
                        <ContactForm />
                    </div>
                    <aside className="lg:col-span-5">
                        <div
                            className="border rounded-2xl p-7 space-y-5"
                            style={{ background: "linear-gradient(180deg, var(--surface), var(--ink-2))", borderColor: "var(--line-soft)" }}
                            data-testid="contact-info-panel"
                        >
                            {anyContact ? (
                                <>
                                    <h2 className="font-display" style={{ fontSize: 20, fontWeight: 600, color: "var(--text)" }}>Reach us directly</h2>
                                    <ul className="space-y-4">
                                        {c.email && (
                                            <li className="flex items-start gap-3" data-testid="contact-row-email">
                                                <Mail className="w-4 h-4 mt-1" style={{ color: "var(--muted-2)" }} />
                                                <div>
                                                    <div className="eyebrow">— EMAIL</div>
                                                    <a href={`mailto:${c.email}`} style={{ color: "var(--text)", fontWeight: 500 }}>{c.email}</a>
                                                </div>
                                            </li>
                                        )}
                                        {c.phone && (
                                            <li className="flex items-start gap-3" data-testid="contact-row-phone">
                                                <Phone className="w-4 h-4 mt-1" style={{ color: "var(--muted-2)" }} />
                                                <div>
                                                    <div className="eyebrow">— PHONE</div>
                                                    <a href={`tel:${c.phone.replace(/\s/g, "")}`} style={{ color: "var(--text)", fontWeight: 500 }}>{c.phone}</a>
                                                </div>
                                            </li>
                                        )}
                                        {c.address && (
                                            <li className="flex items-start gap-3" data-testid="contact-row-address">
                                                <MapPin className="w-4 h-4 mt-1" style={{ color: "var(--muted-2)" }} />
                                                <div>
                                                    <div className="eyebrow">— OFFICE</div>
                                                    <div style={{ color: "var(--text)", fontWeight: 500 }}>{c.address}</div>
                                                </div>
                                            </li>
                                        )}
                                    </ul>
                                    {waHref && (
                                        <a
                                            href={waHref}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            className="btn-primary"
                                            style={{ background: "linear-gradient(150deg,#2de2c6,#14b892)", boxShadow: "none", width: "100%", justifyContent: "center" }}
                                            data-testid="contact-whatsapp-btn"
                                        >
                                            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                                        </a>
                                    )}
                                    <p style={{ fontSize: 12.5, color: "var(--muted-2)" }}>
                                        We usually reply within one working day, Monday to Friday, 9am – 6pm.
                                    </p>
                                </>
                            ) : (
                                <div data-testid="contact-info-fallback">
                                    <div className="eyebrow">— HOW TO REACH US</div>
                                    <p style={{ color: "var(--muted)", fontSize: 15, marginTop: 12, lineHeight: 1.6 }}>
                                        Use the form on the right — we reply to every enquiry within one working day.
                                    </p>
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}
